import { Checkbox } from "@newsela/angelou";
import { withTrackEvent } from "@newsela/snitch";

import { useSearchAdminPermissions } from "@/search/NewSearchPageMVP/provider/hooks";
import { TopicsDataQASelector } from "../../Topics.constants";
import { TaxonomyLevelEnum } from "../TaxonomyTags.constants";
import { useTaxonomyTagsContext } from "../TaxonomyTags.provider";
import { ITaxonomyContent } from "../TaxonomyTags.types";
import { countAllDescendants } from "./RecursiveTags.utils";
import {
  columnsLayout,
  fieldsetCss,
  innerTagStyle,
  recursiveTagCheckboxCss,
  recursiveTagLabelCss,
} from "./RecursiveTags.styles";

interface RecursiveTagsProps {
  categorizedData: { [key: string]: ITaxonomyContent[] };
  parentId: string;
  level: number;
}

const CheckboxWithTrackEvent = withTrackEvent(
  Checkbox,
) as React.ForwardRefExoticComponent<
  React.ComponentProps<typeof Checkbox> & React.RefAttributes<HTMLInputElement>
>;

const getEventProperties = (tagLabel: string) => ({
  objectType: "Content",
  actionPrefix: "Initiate",
  actionName: "Filter",
  streamProperties: {
    description: `Filter search by Taxonomy ${tagLabel}`,
  },
});

export const RecursiveTags = ({
  categorizedData,
  parentId,
  level,
}: RecursiveTagsProps) => {
  const { isChecked, toggleSelection } = useTaxonomyTagsContext();
  const { blockActionAndShowConfirmationModal, blockFilterChanges } =
    useSearchAdminPermissions();

  let filteredTags =
    categorizedData[`level${level}`]?.filter(
      (tag) => tag.parentId === parentId,
    ) || [];

  /**
   * Level 2 tags are the "main containers" inside the collapsibles
   * To have a better display, we want to sort them by size, showing largest to smallest
   */
  if (level === TaxonomyLevelEnum.MAIN_CATEGORY_LEVEL) {
    filteredTags = filteredTags.sort(
      (a, b) =>
        countAllDescendants(b.id, categorizedData) -
        countAllDescendants(a.id, categorizedData),
    );
  }

  const handleChange = (tagId: string) => {
    if (blockFilterChanges) {
      blockActionAndShowConfirmationModal();
      return;
    }

    toggleSelection(tagId, false);
  };

  return (
    <fieldset css={fieldsetCss(level)}>
      <div css={columnsLayout(level)}>
        {filteredTags.map((tag) => (
          <div key={tag.id} css={innerTagStyle}>
            <CheckboxWithTrackEvent
              onChange={() => handleChange(tag.id)}
              key={tag.id}
              label={tag.label}
              value={tag.value}
              checked={isChecked(tag.id)}
              {...getEventProperties(tag.label)}
              __cssFor={{
                root: recursiveTagCheckboxCss,
                labelText: recursiveTagLabelCss(level),
              }}
              dataProps={{
                "data-qa-selector": `${TopicsDataQASelector.SELECT_TAXONOMY_LEVEL}-${level}`,
                "data-testid": `checkbox-${tag.value}`,
              }}
            />
            {tag.children && tag.children.length > 0 && (
              <RecursiveTags
                categorizedData={categorizedData}
                parentId={tag.id}
                level={level + 1}
              />
            )}
          </div>
        ))}
      </div>
    </fieldset>
  );
};

export default RecursiveTags;
